import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import './AdminPanel.css';

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1)); break;
      }
    }
  }
  return cookieValue;
}

const EMPTY_AP = { titulo: '', apresentador_principal: '', lista_autores: '', horario_inicio: '09:00', horario_fim: '09:05', totem: '' };

function SessaoDetalhe() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const returnCategoria = searchParams.get('returnCategoria');
  const returnDia = searchParams.get('returnDia');
  const voltarPara = returnCategoria && returnDia
    ? `/admin-panel?categoria=${encodeURIComponent(returnCategoria)}&dia=${encodeURIComponent(returnDia)}`
    : '/admin-panel';
  const { user } = useAuth();
  const [sessao, setSessao] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_AP, sessao: id });
  const [showForm, setShowForm] = useState(false);
  const [editingAp, setEditingAp] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [todosUsuarios, setTodosUsuarios] = useState([]);
  const [showAvaliadores, setShowAvaliadores] = useState(false);
  const [totemSelecionado, setTotemSelecionado] = useState(1);
  const [avaliadoresSelecionados, setAvaliadoresSelecionados] = useState([]);
  const [salvandoAvaliadores, setSalvandoAvaliadores] = useState(false);
  const [buscaAvaliador, setBuscaAvaliador] = useState('');
  const [buscaFocada, setBuscaFocada] = useState(false);
  const [controlandoAvaliacao, setControlandoAvaliacao] = useState(null);
  const buscaRef = useRef(null);

  useEffect(() => {
    function handleClickFora(e) {
      if (buscaRef.current && !buscaRef.current.contains(e.target)) {
        setBuscaFocada(false);
      }
    }
    document.addEventListener('mousedown', handleClickFora);
    return () => document.removeEventListener('mousedown', handleClickFora);
  }, []);
  const csrfHeaders = { headers: { 'X-CSRFToken': getCookie('csrftoken') } };

  const fetchSessao = useCallback(() => {
    axios.get(`/api/sessoes/${id}/`)
      .then(r => setSessao(r.data))
      .catch(() => toast.error('Não foi possível carregar a sessão.'));
  }, [id]);

  useEffect(() => { fetchSessao(); }, [fetchSessao]);

  useEffect(() => {
    axios.get('/api/usuarios/')
      .then(r => setTodosUsuarios(r.data))
      .catch(() => {});
  }, []);

  // Abrir painel de avaliadores automaticamente para is_admin_premio
  useEffect(() => {
    if (user?.is_admin_premio) setShowAvaliadores(true);
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
  };

  const handleCreate = (e) => {
    e.preventDefault();
    axios.post('/api/apresentacoes/', { ...formData, sessao: id }, csrfHeaders)
      .then(() => {
        toast.success('Apresentação adicionada!');
        setFormData({ ...EMPTY_AP, sessao: id });
        setShowForm(false);
        fetchSessao();
      })
      .catch(() => toast.error('Erro ao adicionar apresentação.'));
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    axios.put(`/api/apresentacoes/${editingAp.id}/`, editingAp, csrfHeaders)
      .then(() => {
        toast.success('Apresentação atualizada!');
        setEditingAp(null);
        fetchSessao();
      })
      .catch(() => toast.error('Erro ao atualizar apresentação.'));
  };

  const handleDelete = (ap) => {
    axios.delete(`/api/apresentacoes/${ap.id}/`, csrfHeaders)
      .then(() => {
        toast.success('Apresentação removida.');
        setConfirmDelete(null);
        fetchSessao();
      })
      .catch(() => toast.error('Erro ao remover apresentação.'));
  };

  const handleAdicionarAvaliadores = () => {
    if (avaliadoresSelecionados.length === 0) return;
    const grupoTotem = (sessao.totens_avaliadores || []).find(g => g.totem === totemSelecionado);
    const jaVinculados = sessao.categoria === 'posteres' ? (grupoTotem?.avaliadores || []) : (sessao.avaliadores || []);
    const novosIds = [...new Set([...jaVinculados, ...avaliadoresSelecionados.map(Number)])];
    const requisicao = sessao.categoria === 'posteres'
      ? axios.put(`/api/sessoes/${id}/totens/${totemSelecionado}/avaliadores/`, { avaliadores: novosIds }, csrfHeaders)
      : axios.put(`/api/sessoes/${id}/`, { ...sessao, avaliadores: novosIds }, csrfHeaders);
    setSalvandoAvaliadores(true);
    requisicao
      .then(() => {
        toast.success(`${avaliadoresSelecionados.length} avaliador(es) vinculado(s) à sessão!`);
        setAvaliadoresSelecionados([]);
        setBuscaFocada(false);
        setBuscaAvaliador('');
        fetchSessao();
      })
      .catch(() => toast.error('Erro ao vincular os avaliadores.'))
      .finally(() => setSalvandoAvaliadores(false));
  };

  const handleRemoverAvaliador = (userId) => {
    const grupoTotem = (sessao.totens_avaliadores || []).find(g => g.totem === totemSelecionado);
    const atuais = sessao.categoria === 'posteres' ? (grupoTotem?.avaliadores || []) : (sessao.avaliadores || []);
    const novosIds = atuais.filter(a => a !== userId);
    const requisicao = sessao.categoria === 'posteres'
      ? axios.put(`/api/sessoes/${id}/totens/${totemSelecionado}/avaliadores/`, { avaliadores: novosIds }, csrfHeaders)
      : axios.put(`/api/sessoes/${id}/`, { ...sessao, avaliadores: novosIds }, csrfHeaders);
    requisicao
      .then(() => {
        toast.success('Avaliador removido da sessão.');
        fetchSessao();
      })
      .catch(() => toast.error('Erro ao remover avaliador.'));
  };

  const handleControleAvaliacao = (apresentacao, acao) => {
    setControlandoAvaliacao(apresentacao.id);
    axios.post(`/api/apresentacoes/${apresentacao.id}/controle-avaliacao/`, { acao }, csrfHeaders)
      .then(r => {
        toast.success(r.data.mensagem);
        fetchSessao();
      })
      .catch(() => toast.error('Não foi possível alterar o estado da avaliação.'))
      .finally(() => setControlandoAvaliacao(null));
  };

  const handleControleTodas = async (acao) => {
    const alvo = aps.filter(ap => ap.avaliacao_aberta !== (acao === 'abrir'));
    if (alvo.length === 0) {
      toast.info(acao === 'abrir' ? 'Todas as avaliações já estão abertas.' : 'Todas as avaliações já estão fechadas.');
      return;
    }
    setControlandoAvaliacao('todas');
    try {
      await Promise.all(alvo.map(ap => axios.post(
        `/api/apresentacoes/${ap.id}/controle-avaliacao/`, { acao }, csrfHeaders
      )));
      toast.success(`${alvo.length} avaliação(ões) ${acao === 'abrir' ? 'aberta(s)' : 'fechada(s)'} com sucesso.`);
      fetchSessao();
    } catch {
      toast.error('Não foi possível alterar todas as avaliações. Confira os estados individualmente.');
      fetchSessao();
    } finally {
      setControlandoAvaliacao(null);
    }
  };

  if (!sessao) return <div className="admin-container"><p>Carregando…</p></div>;

  const aps = sessao.apresentacoes || [];
  const grupoTotemSelecionado = (sessao.totens_avaliadores || []).find(g => g.totem === totemSelecionado);
  const avaliadoresVinculados = sessao.categoria === 'posteres'
    ? (grupoTotemSelecionado?.avaliadores_detalhes || [])
    : (sessao.avaliadores_detalhes || []);
  const idsVinculados = sessao.categoria === 'posteres'
    ? (grupoTotemSelecionado?.avaliadores || [])
    : (sessao.avaliadores || []);
  const avaliadoresDisponiveis = todosUsuarios
    .filter(u => u.is_active && !idsVinculados.includes(u.id))
    .sort((a, b) => {
      const nomeA = `${a.first_name} ${a.last_name}`.trim().toLowerCase();
      const nomeB = `${b.first_name} ${b.last_name}`.trim().toLowerCase();
      return nomeA.localeCompare(nomeB, 'pt-BR');
    });

  // Todos os perfis de administração podem gerenciar avaliadores de qualquer categoria
  const podeGerenciarAvaliadores = user?.is_superuser || user?.is_coordenador ||
    user?.is_admin_premio || user?.is_staff;
  const podeControlarAvaliacao = podeGerenciarAvaliadores;
  const podeEditarApresentacoes = podeGerenciarAvaliadores;
  const podeExcluirApresentacoes = (user?.is_superuser || user?.is_coordenador || user?.is_staff) && !user?.is_admin_premio;

  return (
    <div className="admin-container">
      <div className="sd-breadcrumb">
        <Link to={voltarPara} className="sd-back">← Voltar ao Painel</Link>
      </div>

      {/* Header da sessão */}
      <div className="sd-session-header">
        <div className="sd-session-badge">{sessao.nome}</div>
        <h1 className="sd-session-title">{sessao.tema}</h1>
        <div className="sd-session-meta">
          <span>📅 {new Date(sessao.dia).toLocaleDateString('pt-BR', { timeZone: 'UTC', weekday: 'long', day: '2-digit', month: 'long' })}</span>
          <span>🕐 {sessao.horario_inicio?.substring(0,5)} – {sessao.horario_fim?.substring(0,5)}</span>
          <span>📋 {aps.length} apresentação(ões)</span>
          <span>{{temas_livres:'📋 Temas Livres', posteres:'🖼️ Pôsteres', temas_correlatos:'🎤 Temas Correlatos'}[sessao.categoria] || sessao.categoria}</span>
        </div>
      </div>

      {/* AVALIADORES DA SESSÃO */}
      <div className="sd-avaliadores-card">
        {sessao.categoria === 'posteres' && (
          <div style={{ display: 'flex', gap: '8px', padding: '14px 18px 0', flexWrap: 'wrap' }}>
            {[1, 2, 3, 4, 5, 6].map(numero => {
              const total = (sessao.totens_avaliadores || []).find(g => g.totem === numero)?.avaliadores.length || 0;
              return <button key={numero} className={numero === totemSelecionado ? 'ap-btn-primary' : 'ap-btn-action'} onClick={() => setTotemSelecionado(numero)}>
                Totem {numero} · {total} avaliador(es)
              </button>;
            })}
          </div>
        )}
        <div className="sd-avaliadores-header" onClick={() => setShowAvaliadores(!showAvaliadores)}>
          <div>
            <span className="sd-avaliadores-icon">👥</span>
            <span className="sd-avaliadores-title">
              {sessao.categoria === 'posteres' ? `Avaliadores do Totem ${totemSelecionado}` : 'Avaliadores desta Sessão'}
            </span>
            <span className="sd-avaliadores-count">{avaliadoresVinculados.length} vinculado(s)</span>
          </div>
          <span className="sd-avaliadores-toggle">{showAvaliadores ? '▲' : '▼'}</span>
        </div>

        {showAvaliadores && (
          <div className="sd-avaliadores-body">
            {/* Lista de avaliadores vinculados */}
            {avaliadoresVinculados.length === 0 ? (
              <p className="sd-avaliadores-empty">Nenhum avaliador vinculado ainda.</p>
            ) : (
              <div className="sd-avaliadores-list">
                {avaliadoresVinculados.map(u => (
                  <div key={u.id} className="sd-avaliador-tag">
                    <span>👤 {u.first_name || u.username} {u.last_name}</span>
                    <span className="sd-avaliador-login">@{u.username}</span>
                    {podeGerenciarAvaliadores && (
                      <button className="sd-avaliador-remove" onClick={() => handleRemoverAvaliador(u.id)} title="Remover">✕</button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Adicionar avaliador */}
            {podeGerenciarAvaliadores && (
              <div style={{ marginTop: '12px' }}>
                <button
                  className="ap-btn-primary"
                  onClick={() => { setBuscaFocada(true); setBuscaAvaliador(''); setAvaliadoresSelecionados([]); }}
                >
                  + Vincular Avaliadores
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* MODAL DE SELEÇÃO DE AVALIADOR */}
      {buscaFocada && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setBuscaFocada(false); }}
        >
          <div style={{ background: 'white', borderRadius: '12px', width: '480px', maxWidth: '95vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            {/* Header do modal */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: '#1a2e45', fontSize: '18px' }}>👥 Selecionar Avaliadores</h3>
              <button onClick={() => setBuscaFocada(false)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#888', lineHeight: 1 }}>✕</button>
            </div>

            {/* Campo de busca */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #eee' }}>
              <input
                type="text"
                placeholder="🔍 Digite o nome ou usuário..."
                value={buscaAvaliador}
                onChange={e => setBuscaAvaliador(e.target.value)}
                autoFocus
                style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #c9a84c', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
              />
              <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#1a2e45', fontSize: '13px', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={(() => {
                      const visiveis = avaliadoresDisponiveis.filter(u => {
                        if (!buscaAvaliador.trim()) return true;
                        const termo = buscaAvaliador.toLowerCase();
                        return `${u.first_name} ${u.last_name}`.toLowerCase().includes(termo) || u.username.toLowerCase().includes(termo);
                      });
                      return visiveis.length > 0 && visiveis.every(u => avaliadoresSelecionados.includes(u.id));
                    })()}
                    onChange={e => {
                      const visiveis = avaliadoresDisponiveis.filter(u => {
                        if (!buscaAvaliador.trim()) return true;
                        const termo = buscaAvaliador.toLowerCase();
                        return `${u.first_name} ${u.last_name}`.toLowerCase().includes(termo) || u.username.toLowerCase().includes(termo);
                      }).map(u => u.id);
                      setAvaliadoresSelecionados(atuais => e.target.checked
                        ? [...new Set([...atuais, ...visiveis])]
                        : atuais.filter(idAtual => !visiveis.includes(idAtual)));
                    }}
                  />
                  Selecionar todos os exibidos
                </label>
                <span style={{ fontSize: '12px', color: '#6b7a95' }}>{avaliadoresSelecionados.length} selecionado(s)</span>
              </div>
            </div>

            {/* Lista de avaliadores */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {avaliadoresDisponiveis
                .filter(u => {
                  if (!buscaAvaliador.trim()) return true;
                  const termo = buscaAvaliador.toLowerCase();
                  const nome = `${u.first_name} ${u.last_name}`.toLowerCase();
                  return nome.includes(termo) || u.username.toLowerCase().includes(termo);
                })
                .map(u => (
                  <div
                    key={u.id}
                    onClick={() => setAvaliadoresSelecionados(atuais => atuais.includes(u.id)
                      ? atuais.filter(idAtual => idAtual !== u.id)
                      : [...atuais, u.id])}
                    style={{
                      padding: '12px 24px', cursor: 'pointer',
                      borderBottom: '1px solid #f5f5f5',
                      display: 'flex', alignItems: 'center', gap: '12px',
                      transition: 'background 0.15s',
                      background: avaliadoresSelecionados.includes(u.id) ? '#eef5ff' : 'white',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={avaliadoresSelecionados.includes(u.id)}
                      onChange={() => setAvaliadoresSelecionados(atuais => atuais.includes(u.id)
                        ? atuais.filter(idAtual => idAtual !== u.id)
                        : [...atuais, u.id])}
                      onClick={e => e.stopPropagation()}
                      aria-label={`Selecionar ${`${u.first_name} ${u.last_name}`.trim() || u.username}`}
                      style={{ width: '18px', height: '18px', accentColor: '#285f9e', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '24px' }}>👤</span>
                    <div>
                      <div style={{ fontWeight: '600', color: '#1a2e45', fontSize: '15px' }}>
                        {u.first_name} {u.last_name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>@{u.username}</div>
                    </div>
                  </div>
                ))
              }
              {avaliadoresDisponiveis.filter(u => {
                if (!buscaAvaliador.trim()) return true;
                const termo = buscaAvaliador.toLowerCase();
                const nome = `${u.first_name} ${u.last_name}`.toLowerCase();
                return nome.includes(termo) || u.username.toLowerCase().includes(termo);
              }).length === 0 && (
                <div style={{ padding: '24px', textAlign: 'center', color: '#888', fontSize: '14px' }}>
                  Nenhum avaliador encontrado.
                </div>
              )}
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="ap-btn-ghost" onClick={() => { setBuscaFocada(false); setAvaliadoresSelecionados([]); }} disabled={salvandoAvaliadores}>
                Cancelar
              </button>
              <button className="ap-btn-primary" onClick={handleAdicionarAvaliadores} disabled={avaliadoresSelecionados.length === 0 || salvandoAvaliadores}>
                {salvandoAvaliadores ? 'Vinculando...' : `Vincular selecionados (${avaliadoresSelecionados.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Botão Adicionar Apresentação */}
      <div className="sd-toolbar">
        <h2 className="sd-section-title">Apresentações</h2>
        {!user?.is_admin_premio && (
          <button className="ap-btn-new" onClick={() => setShowForm(true)}>+ Adicionar Apresentação</button>
        )}
      </div>

      {/* Modal: Criar apresentação */}
      {showForm && (
        <div className="ap-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="ap-modal" onClick={e => e.stopPropagation()}>
            <div className="ap-modal-header">
              <h2>Nova Apresentação</h2>
              <button className="ap-modal-close" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <form onSubmit={handleCreate} className="ap-form">
              <div className="ap-field">
                <label>Título do Trabalho *</label>
                <input name="titulo" value={formData.titulo} onChange={handleChange} placeholder="Título completo" required autoFocus />
              </div>
              <div className="ap-field">
                <label>Apresentador Principal *</label>
                <input name="apresentador_principal" value={formData.apresentador_principal} onChange={handleChange} placeholder="Nome" required />
              </div>
              <div className="ap-field">
                <label>Lista de Autores</label>
                <textarea name="lista_autores" value={formData.lista_autores} onChange={handleChange}
                  placeholder="Autores separados por ponto e vírgula" rows={2} className="ap-textarea" />
              </div>
              <div className="ap-form-row">
                <div className="ap-field">
                  <label>Horário Início *</label>
                  <input name="horario_inicio" type="time" data-format="HH:mm" value={formData.horario_inicio} onChange={handleChange} required />
                </div>
                <div className="ap-field">
                  <label>Horário Fim *</label>
                  <input name="horario_fim" type="time" data-format="HH:mm" value={formData.horario_fim} onChange={handleChange} required />
                </div>
              </div>
              {sessao.categoria === 'posteres' && (
                <div className="ap-field">
                  <label>Nº do Totem (1 a 6) *</label>
                  <input name="totem" type="number" min="1" max="6" value={formData.totem || ''} onChange={handleChange} required />
                </div>
              )}
              <div className="ap-form-actions">
                <button type="submit" className="ap-btn-primary">✓ Adicionar</button>
                <button type="button" className="ap-btn-ghost" onClick={() => setShowForm(false)}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Editar apresentação */}
      {editingAp && (
        <div className="ap-modal-overlay" onClick={() => setEditingAp(null)}>
          <div className="ap-modal" onClick={e => e.stopPropagation()}>
            <div className="ap-modal-header">
              <h2>✏️ Editar Apresentação</h2>
              <button className="ap-modal-close" onClick={() => setEditingAp(null)}>✕</button>
            </div>
            <form onSubmit={handleUpdate} className="ap-form">
              <div className="ap-field">
                <label>Título *</label>
                <input value={editingAp.titulo} onChange={e => setEditingAp(p => ({...p, titulo: e.target.value}))} required autoFocus />
              </div>
              <div className="ap-field">
                <label>Apresentador Principal *</label>
                <input value={editingAp.apresentador_principal} onChange={e => setEditingAp(p => ({...p, apresentador_principal: e.target.value}))} required />
              </div>
              <div className="ap-field">
                <label>Lista de Autores</label>
                <textarea value={editingAp.lista_autores} onChange={e => setEditingAp(p => ({...p, lista_autores: e.target.value}))} rows={2} className="ap-textarea" />
              </div>
              <div className="ap-form-row">
                <div className="ap-field">
                  <label>Início *</label>
                  <input type="time" data-format="HH:mm" value={editingAp.horario_inicio} onChange={e => setEditingAp(p => ({...p, horario_inicio: e.target.value}))} required />
                </div>
                <div className="ap-field">
                  <label>Fim *</label>
                  <input type="time" data-format="HH:mm" value={editingAp.horario_fim} onChange={e => setEditingAp(p => ({...p, horario_fim: e.target.value}))} required />
                </div>
              </div>
              {sessao.categoria === 'posteres' && (
                <div className="ap-field">
                  <label>Nº do Totem (1 a 6) *</label>
                  <input type="number" min="1" max="6" value={editingAp.totem || ''} onChange={e => setEditingAp(p => ({...p, totem: e.target.value}))} required />
                </div>
              )}
              <div className="ap-form-actions">
                <button type="submit" className="ap-btn-primary">✓ Salvar</button>
                <button type="button" className="ap-btn-ghost" onClick={() => setEditingAp(null)}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Confirmar Delete */}
      {confirmDelete && (
        <div className="ap-modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="ap-modal ap-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="ap-modal-header ap-modal-danger">
              <h2>⚠️ Remover Apresentação</h2>
              <button className="ap-modal-close" onClick={() => setConfirmDelete(null)}>✕</button>
            </div>
            <p className="ap-modal-desc">
              Remover <strong>"{confirmDelete.titulo}"</strong>?<br />
              <span className="ap-warn">As avaliações também serão removidas.</span>
            </p>
            <div className="ap-form-actions ap-form-actions-pad">
              <button className="ap-btn-danger" onClick={() => handleDelete(confirmDelete)}>🗑 Remover</button>
              <button className="ap-btn-ghost" onClick={() => setConfirmDelete(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {aps.length > 0 && podeControlarAvaliacao && (
        <div className="sd-avaliacao-control">
          <div>
            <span className="sd-avaliacao-eyebrow">CONTROLE DA COMISSÃO</span>
            <h2>Abertura das avaliações</h2>
            <p>Abra os trabalhos no momento da apresentação. Enquanto estiverem fechados, o avaliador verá “Não Avaliado”.</p>
          </div>
          <div className="sd-avaliacao-summary">
            <span><strong>{aps.filter(ap => ap.avaliacao_aberta).length}</strong> abertas</span>
            <span><strong>{aps.filter(ap => !ap.avaliacao_aberta).length}</strong> fechadas</span>
          </div>
          <div className="sd-avaliacao-bulk">
            <button className="sd-btn-open-all" onClick={() => handleControleTodas('abrir')} disabled={controlandoAvaliacao !== null}>▶ Abrir todas</button>
            <button className="sd-btn-close-all" onClick={() => handleControleTodas('fechar')} disabled={controlandoAvaliacao !== null}>■ Fechar todas</button>
          </div>
        </div>
      )}

      {/* Lista de apresentações */}
      {aps.length === 0 ? (
        <div className="ap-empty">Nenhuma apresentação ainda. Adicione a primeira!</div>
      ) : (
        <div className="sd-ap-list">
          {aps.map((ap, idx) => (
            <div key={ap.id} className="sd-ap-card">
              <div className="sd-ap-num">{String(idx + 1).padStart(2, '0')}</div>
              <div className="sd-ap-body">
                <div className="sd-ap-titulo">{ap.titulo}</div>
                <div className="sd-ap-meta">
                  <span className="sd-ap-apresentador">👤 {ap.apresentador_principal}</span>
                  <span className="sd-ap-horario">🕐 {ap.horario_inicio?.substring(0,5)}–{ap.horario_fim?.substring(0,5)}</span>
                  {ap.totem && <span className="sd-ap-totem">🖼️ Totem {ap.totem}</span>}
                  {ap.avaliacoes?.length > 0 && (
                    <span className="sd-ap-avals">⭐ {ap.avaliacoes.length} avaliação(ões)</span>
                  )}
                </div>
                {ap.lista_autores && <div className="sd-ap-autores">👥 {ap.lista_autores}</div>}
              </div>
              <div className="sd-ap-actions">
                {podeControlarAvaliacao && (
                  <button
                    className={`sd-btn-avaliacao ${ap.avaliacao_aberta ? 'is-open' : 'is-closed'}`}
                    onClick={() => handleControleAvaliacao(ap, ap.avaliacao_aberta ? 'fechar' : 'abrir')}
                    disabled={controlandoAvaliacao !== null}
                    title={ap.avaliacao_aberta ? 'Fechar esta avaliação' : 'Abrir esta avaliação'}
                  >
                    {controlandoAvaliacao === ap.id
                      ? 'Aguarde…'
                      : ap.avaliacao_aberta ? '● Aberta · Fechar' : '▶ Abrir avaliação'}
                  </button>
                )}
                {podeEditarApresentacoes && (
                  <button className="ap-btn-action ap-btn-edit" onClick={() => setEditingAp({...ap})} title="Editar apresentação">✏️</button>
                )}
                {podeExcluirApresentacoes && (
                  <button className="ap-btn-action ap-btn-del" onClick={() => setConfirmDelete(ap)} title="Remover">🗑</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SessaoDetalhe;
